import {
  AutocompleteRenderInputParams,
  InputAdornment,
  Tooltip,
  useTheme,
} from '@mui/material';
import { AutocompleteStyled, IconBoxStyled } from './j-auto-complete.styled';
import React, { SyntheticEvent } from 'react';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import IOption from '@models/option';
import { IconSize } from '@constants';
import JTextFieldBase from '@atoms/JTextFieldBase';
import { useTranslation } from 'react-i18next';

interface IProps {
  options: IOption[];
  value?: IOption[] | IOption | null;
  defaultValue?: IOption[] | IOption;
  onChange: (_: SyntheticEvent<Element, Event>, newValue: unknown) => void;
  onTextChange: (_: string) => void;
  label: string;
  fullWidth?: boolean;
  multiple?: boolean;
  required?: boolean;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  endIcon?: JSX.Element;
  disabled?: boolean;
  whiteBg?: boolean;
}

export const JAutocomplete: React.FC<IProps> = ({
  options,
  value,
  onChange,
  onTextChange,
  label,
  multiple = false,
  renderInput,
  fullWidth,
  defaultValue,
  endIcon,
  disabled,
  required,
  whiteBg = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const checkKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  const defaultInput = (params: AutocompleteRenderInputParams) => (
    <JTextFieldBase
      required={required}
      label={label}
      attr={{ whiteBg }}
      {...params}
      onChange={(event) => {
        onTextChange(event.target.value);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
        }
      }}
      InputProps={{
        ...params.InputProps,
        onKeyDown: (ev) => checkKeyDown(ev),
        endAdornment: (
          <IconBoxStyled>
            <InputAdornment position="end">
              {endIcon ?? (
                <ChevronDownIcon
                  color={theme.palette.primary.main}
                  width={IconSize.sm}
                />
              )}
            </InputAdornment>
          </IconBoxStyled>
        ),
      }}
      disabled={disabled}
    />
  );
  const input = renderInput ?? defaultInput;
  return (
    <Tooltip title={t('AUTOCOMPLETE_TOOLTIP')} placement="top-end" arrow>
      <AutocompleteStyled
        data-qa={`autocomplete-${label}`}
        size="small"
        fullWidth={fullWidth}
        options={
          Array.isArray(options) && Array.isArray(value)
            ? options.filter(
                (item) =>
                  !value?.some((valueItem) => valueItem.value === item.value),
              )
            : options
        }
        defaultValue={defaultValue}
        value={value}
        multiple={multiple}
        freeSolo
        onChange={onChange}
        renderInput={input}
      />
    </Tooltip>
  );
};
